import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CalendarDays, ExternalLink, Tag as TagIcon } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { TagEntityPopover } from "@/components/TagEntityPopover";

function relTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "à l'instant";
  if (diff < 3600) return `${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} h`;
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
}

const TYPE_LABEL = {
  article: "Article", portrait: "Portrait", interview: "Interview",
  resultat: "Résultat", annonce: "Annonce", communique: "Communiqué",
  reportage_photo: "Photos", video: "Vidéo", live: "Live",
  resume: "Résumé", appel_sponsor: "Appel sponsor", billet_contributeur: "Contributeur",
  // Curator categories
  actualite: "Actualité", resultats: "Résultats", competition_a_venir: "À venir",
  retour_competition: "Retour compétition", billet_humeur: "Billet d'humeur",
  soiree: "Soirée", communique_club: "Communiqué club", communique_federation: "Communiqué fédé",
  galerie: "Galerie", autre: "Autre",
};

// V9.21 — Sprint UX Simplification : titre robuste anti-URL brute.
// Ne jamais afficher un titre qui est en réalité une URL. Fallback :
// 1) title éditorial (non-URL) → 2) source_name → 3) libellé neutre selon catégorie
const URL_LIKE_RE = /^https?:\/\//i;
function safeNewsTitle(item) {
  const raw = (item?.title || "").trim();
  const looksLikeUrl = URL_LIKE_RE.test(raw) || (raw.length > 0 && raw.includes("/") && !raw.includes(" "));
  if (raw && !looksLikeUrl) return raw;
  if (item?.source_name) {
    const cat = item?.category || item?.type || "";
    if (cat === "video" || cat === "galerie" || cat === "reportage_photo") {
      return `Vidéo ${item.source_name}`;
    }
    return `Article ${item.source_name}`;
  }
  const cat = item?.category || item?.type;
  if (cat === "video") return "Contenu vidéo";
  if (cat === "galerie" || cat === "reportage_photo") return "Galerie photo";
  return "Article externe";
}

export function NewsCard({ item, compact = false, onItemUpdated }) {
  const { user } = useAuth();
  const isAdmin = !!user && (user.roles || []).some((r) => ["admin", "copf"].includes(r));
  // V9.21 — Sprint UX Simplification : le bouton "Tag" est réservé aux admins/COPF
  // pour éviter d'exposer un contrôle technique sur toutes les cartes publiques.
  const canTag = isAdmin;
  if (!item) return null;
  const fallback = "https://images.unsplash.com/photo-1659346330058-e4aa255e3fc5?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85";
  // Compatibilité curator_items + news legacy + V9.11 user articles
  const img = item.image_url || item.cover_image_url || item.cover_url || fallback;
  const category = item.category || item.type || (item.kind === "user_article" ? "billet_contributeur" : undefined);
  // Ne pas afficher un badge type inconnu / vide / placeholder
  const categoryLabel = category ? (TYPE_LABEL[category] || (String(category).toLowerCase() === "tag" ? null : category)) : null;
  const title = safeNewsTitle(item);
  // V9.11 — Les articles contributeurs ont source="user" et utilisent un slug préfixé `contrib-`
  let href;
  if (item.source === "user" || item.kind === "user_article") {
    href = `/actualites/contrib-${item.slug || item.id}`;
  } else {
    href = item.url || (item.slug ? `/actualites/${item.slug}` : `/actualites/${item.id}`);
  }
  const isExternal = item.url && /^https?:\/\//i.test(item.url) && item.source !== "user" && item.kind !== "user_article";
  // V9.5.2 — date éditoriale prioritaire :
  //   published_at_source (date de l'article source) > published_at > created_at
  const date = item.published_at_source || item.published_at || item.created_at;
  const Wrapper = isExternal
    ? ({ children }) => <a href={href} target="_blank" rel="noreferrer" data-testid="news-feed-item" className="group block">{children}</a>
    : ({ children }) => <Link to={href} data-testid="news-feed-item" className="group block">{children}</Link>;
  return (
    <Wrapper>
      <Card className={`overflow-hidden aito-card-hover ${compact ? "flex flex-row gap-3" : ""}`}>
        <div className={`relative ${compact ? "w-28 sm:w-40 shrink-0" : "aspect-[2/1] sm:aspect-[16/10]"} bg-muted`}>
          <img src={img} alt="" className={`${compact ? "h-full w-full" : "absolute inset-0 h-full w-full"} object-cover`} loading="lazy"
            onError={(e) => { e.currentTarget.src = fallback; }} />
          <div className="absolute left-2 top-2 flex flex-wrap gap-1.5">
            {categoryLabel && (
              <Badge variant="secondary" className="bg-black/70 text-white border-0">{categoryLabel}</Badge>
            )}
            {item.pinned && <Badge className="bg-[hsl(var(--aito-gold))] text-black">Épinglé</Badge>}
            {item.featured && !item.pinned && <Badge className="bg-[hsl(var(--aito-lagoon))] text-black">À la une</Badge>}
            {item.is_official && <Badge className="bg-[hsl(var(--aito-gold))] text-black">Officiel</Badge>}
          </div>
          {/* V9.21 — Sprint UX Simplification : bouton "Tag" admin/COPF uniquement,
              en icône seule pour ne pas surcharger visuellement les cartes publiques. */}
          {canTag && item.id && (
            <div
              className="absolute right-2 top-2 z-10 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
            >
              <TagEntityPopover
                itemId={item.id}
                existing={item.associations || []}
                onAdded={(updated) => onItemUpdated?.(updated)}
                variant="secondary"
                size="icon"
                label={<TagIcon className="h-3.5 w-3.5" aria-label="Tag entité" />}
                target={item.source === "user" || item.kind === "user_article" ? "user_article" : "curator"}
              />
            </div>
          )}
        </div>
        <div className="p-3 sm:p-4 flex-1 min-w-0">
          <h3 className="font-display font-semibold text-base sm:text-lg leading-tight line-clamp-2 break-words [overflow-wrap:anywhere] group-hover:text-[hsl(var(--aito-lagoon))] transition-colors">{title}</h3>
          {item.summary && <p className="mt-1.5 text-sm text-muted-foreground line-clamp-2 break-words [overflow-wrap:anywhere]">{item.summary}</p>}
          {/* Sportif·s & disciplines associés */}
          {((item.tagged_athletes || []).length > 0 || item.discipline) && (
            <div className="mt-2 flex flex-wrap gap-1">
              {item.discipline && (
                <Badge variant="outline" className="text-[10px] capitalize bg-[hsl(var(--aito-lagoon)/0.08)] border-[hsl(var(--aito-lagoon)/0.3)]">
                  {item.discipline.replace(/-/g, " ")}
                </Badge>
              )}
              {(item.tagged_athletes || []).slice(0, 3).map((n) => (
                <Badge key={n} variant="secondary" className="text-[10px]">@ {n}</Badge>
              ))}
            </div>
          )}
          {/* Hashtags */}
          {(item.hashtags || []).length > 0 && (
            <div className="mt-1 text-[11px] text-[hsl(var(--aito-lagoon))] truncate">
              {(item.hashtags || []).slice(0, 4).map((h) => `#${h}`).join(" ")}
            </div>
          )}
          <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
            <div className="flex items-center gap-2 min-w-0">
              {item.source_name && <span className="truncate">{item.source_name}</span>}
              {item.island && <span>· {item.island}</span>}
            </div>
            <span className="flex items-center gap-1"><CalendarDays className="h-3 w-3" />{relTime(date)}</span>
          </div>
          {(item.url || item.source_url) && (
            <div className="mt-2 text-[11px] text-muted-foreground flex items-center gap-1">
              <ExternalLink className="h-3 w-3" /> {item.source_name ? `Lire sur ${item.source_name}` : "Source externe"}
            </div>
          )}
        </div>
      </Card>
    </Wrapper>
  );
}
